import { useCallback, useEffect, useMemo, useState } from 'react';
import { getExpiries, getHolidays } from '../../services/referenceDataService';
import { buildDteMap } from '../../utils/dte';
import type { Expiry } from '../../types/expiry';
import { loadDtePrefs, type DtePrefs } from './DTEControls';

interface UseDteOverlayResult {
  prefs: DtePrefs;
  setPrefs: (next: DtePrefs) => void;
  dteByDate: Map<string, number | null>;
  holidays: Record<string, string>;
  onDatesChange: (dates: string[]) => void;
}

/**
 * Owns the DTE prefs (index, expiry type), the visible date range published
 * by DayTabs, the resulting DTE map, and the trading holidays map. Errors
 * swallowed silently — the overlay is non-essential and any failure simply
 * hides the badges (DTE) or makes holidays look like normal days.
 */
export function useDteOverlay(): UseDteOverlayResult {
  const [prefs, setPrefs] = useState<DtePrefs>(() => loadDtePrefs());
  const [expiries, setExpiries] = useState<Expiry[]>([]);
  const [holidays, setHolidays] = useState<Record<string, string>>({});
  const [dates, setDates] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    getExpiries(prefs.index, prefs.expiryType, 8)
      .then(res => { if (!cancelled) setExpiries(res.expiries); })
      .catch(() => { if (!cancelled) setExpiries([]); });
    return () => { cancelled = true; };
  }, [prefs.index, prefs.expiryType]);

  // Holidays don't depend on prefs — fetch once on mount, cached for 1h.
  useEffect(() => {
    let cancelled = false;
    getHolidays()
      .then(res => { if (!cancelled) setHolidays(res.holidays); })
      .catch(() => { if (!cancelled) setHolidays({}); });
    return () => { cancelled = true; };
  }, []);

  const dteByDate = useMemo(
    () => buildDteMap(dates, expiries, holidays),
    [dates, expiries, holidays],
  );

  const onDatesChange = useCallback((next: string[]) => {
    setDates(prev => {
      if (prev.length === next.length && prev.every((d, i) => d === next[i])) return prev;
      return next;
    });
  }, []);

  return { prefs, setPrefs, dteByDate, holidays, onDatesChange };
}
